import { LEGACY_FARBEN, naechsteLegacyFarbe } from "./farben.ts";

export type Segment = {
  text: string;
  farbe?: string;
  fett?: boolean;
  kursiv?: boolean;
  unterstrichen?: boolean;
  durchgestrichen?: boolean;
  verschleiert?: boolean;
};
export type TextBefund = { stelle: number; meldung: string };

type Deko = "fett" | "kursiv" | "unterstrichen" | "durchgestrichen" | "verschleiert";
type Eintrag = { name: string; farbe?: string; deko?: Deko; an?: boolean };

const DEKO: Record<string, Deko> = {
  bold: "fett", b: "fett",
  italic: "kursiv", i: "kursiv", em: "kursiv",
  underlined: "unterstrichen", u: "unterstrichen",
  strikethrough: "durchgestrichen", st: "durchgestrichen",
  obfuscated: "verschleiert", obf: "verschleiert",
};
const BENANNT: Record<string, string> = Object.fromEntries([
  ...LEGACY_FARBEN.map((l) => [l.name, l.hex]),
  ["dark_grey", "#555555"],
  ["grey", "#AAAAAA"],
]);
const FARB_TAGS = new Set(["color", "colour", "c"]);

function farbeAus(wert: string, rollen: Record<string, string>): string | undefined {
  if (/^#[0-9a-fA-F]{6}$/.test(wert)) return wert.toUpperCase();
  return BENANNT[wert] ?? rollen[wert]?.toUpperCase();
}

/** Liest einen Tag ab Position i (dort steht „<“); null, wenn dort kein Tag beginnt. */
function liesTag(text: string, i: number): { inhalt: string; ende: number } | null {
  const ende = text.indexOf(">", i + 1);
  if (ende < 0) return null;
  const inhalt = text.slice(i + 1, ende);
  if (!/^[!/]?[#a-zA-Z0-9_:.-]+$/.test(inhalt)) return null;
  return { inhalt, ende };
}

function stil(stapel: Eintrag[]): Omit<Segment, "text"> {
  const s: Omit<Segment, "text"> = {};
  for (const e of stapel) {
    if (e.farbe) s.farbe = e.farbe;
    if (e.deko) s[e.deko] = e.an ?? true;
  }
  return s;
}

function gleich(a: Omit<Segment, "text">, b: Omit<Segment, "text">): boolean {
  const k = new Set([...Object.keys(a), ...Object.keys(b)]) as Set<keyof typeof a>;
  return [...k].every((x) => a[x] === b[x]);
}

export function zerlege(text: string, rollen: Record<string, string>): { segmente: Segment[]; befunde: TextBefund[] } {
  const segmente: Segment[] = [];
  const befunde: TextBefund[] = [];
  let stapel: Eintrag[] = [];

  const schreibe = (t: string) => {
    if (!t) return;
    const s = stil(stapel);
    const letztes = segmente.at(-1);
    if (letztes) {
      const { text: _, ...letzterStil } = letztes;
      if (gleich(letzterStil, s)) {
        letztes.text += t;
        return;
      }
    }
    segmente.push({ text: t, ...s });
  };

  let i = 0;
  let puffer = "";
  const leeren = () => {
    schreibe(puffer);
    puffer = "";
  };
  while (i < text.length) {
    const z = text[i]!;
    if (z === "\\" && (text[i + 1] === "<" || text[i + 1] === "\\")) {
      puffer += text[i + 1];
      i += 2;
      continue;
    }
    if (z !== "<") {
      puffer += z;
      i++;
      continue;
    }
    const tag = liesTag(text, i);
    if (!tag) {
      puffer += z;
      i++;
      continue;
    }
    const { inhalt, ende } = tag;
    const schliessen = inhalt.startsWith("/");
    const negiert = inhalt.startsWith("!");
    const roh = schliessen || negiert ? inhalt.slice(1) : inhalt;
    const [name, ...rest] = roh.split(":");
    const arg = rest.join(":");
    const kenn = name!.toLowerCase();

    let erkannt = true;
    let eintrag: Eintrag | null = null;
    if (kenn === "reset" && !schliessen) {
      leeren();
      stapel = [];
      i = ende + 1;
      continue;
    } else if (DEKO[kenn]) {
      eintrag = { name: kenn, deko: DEKO[kenn], an: !negiert };
    } else if (FARB_TAGS.has(kenn)) {
      const farbe = schliessen ? undefined : farbeAus(arg, rollen);
      if (!schliessen && !farbe) erkannt = false;
      else eintrag = { name: "color", farbe };
    } else {
      const farbe = farbeAus(roh, rollen);
      if (farbe || (schliessen && (BENANNT[roh] || rollen[roh] || /^#/.test(roh)))) eintrag = { name: roh, farbe };
      else erkannt = false;
    }

    if (!erkannt) {
      befunde.push({ stelle: i, meldung: `Unbekannter Tag <${inhalt}>` });
      puffer += text.slice(i, ende + 1);
      i = ende + 1;
      continue;
    }
    if (schliessen) {
      const ziel = eintrag!;
      let k = stapel.length - 1;
      for (; k >= 0; k--) {
        const e = stapel[k]!;
        if (ziel.deko ? e.deko === ziel.deko : e.farbe !== undefined && (ziel.name === "color" || e.name === ziel.name)) break;
      }
      // Wie Adventure: ohne Gegenstück bleibt das Tag sichtbar, sonst schließt es alles darin Geöffnete mit.
      if (k < 0) {
        befunde.push({ stelle: i, meldung: `</${roh}> schließt nichts` });
        puffer += text.slice(i, ende + 1);
        i = ende + 1;
        continue;
      }
      leeren();
      stapel = stapel.slice(0, k);
    } else {
      leeren();
      stapel.push(eintrag!);
    }
    i = ende + 1;
  }
  leeren();
  return { segmente, befunde };
}

export function klartext(text: string, rollen: Record<string, string>): string {
  return zerlege(text, rollen).segmente.map((s) => s.text).join("");
}

export function fuerBedrock(segmente: Segment[]): Segment[] {
  return segmente.map((s) => (s.farbe ? { ...s, farbe: naechsteLegacyFarbe(s.farbe).hex } : { ...s }));
}
